import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Clock, X, Minus, BellRing } from 'lucide-react';
import { toast } from 'sonner';

const getPayment = (member, cycle) => {
  const pays = (member.payments || []).filter(p => p.week_number === cycle);
  if (!pays.length) return null;
  return pays.find(p => p.status === 'verified') || pays.find(p => p.status === 'pending') || pays[0];
};

function Cell({ member, cycle, elapsed }) {
  const pay = getPayment(member, cycle);
  if (pay?.status === 'verified') {
    return (
      <a href={pay.proof_url} target="_blank" rel="noopener noreferrer" title={`Validado — $${pay.amount}`}>
        <div className="w-8 h-8 mx-auto rounded-lg bg-green-100 text-green-600 flex items-center justify-center hover:ring-2 ring-green-300 transition-all">
          <Check size={16} />
        </div>
      </a>
    );
  }
  if (pay?.status === 'pending') {
    return (
      <a href={pay.proof_url} target="_blank" rel="noopener noreferrer" title={`Por validar — $${pay.amount}`}>
        <div className="w-8 h-8 mx-auto rounded-lg bg-yellow-100 text-yellow-600 flex items-center justify-center hover:ring-2 ring-yellow-300 transition-all">
          <Clock size={16} />
        </div>
      </a>
    );
  }
  if (pay?.status === 'rejected') {
    return (
      <div title="Rechazado" className="w-8 h-8 mx-auto rounded-lg bg-red-100 text-red-500 flex items-center justify-center">
        <X size={16} />
      </div>
    );
  }
  if (cycle <= elapsed) {
    return (
      <div title="Pago atrasado" className="w-8 h-8 mx-auto rounded-lg bg-red-50 border border-dashed border-red-300 text-red-400 flex items-center justify-center">
        <Minus size={14} />
      </div>
    );
  }
  return (
    <div title="Ciclo futuro" className="w-8 h-8 mx-auto rounded-lg bg-muted/60 text-muted-foreground/40 flex items-center justify-center">
      <Minus size={14} />
    </div>
  );
}

export const PaymentMatrix = ({ members, totalCycles, elapsedCycles, tandaId }) => {
  const { api } = useAuth();
  const [sending, setSending] = useState(false);
  const approved = members.filter(m => m.status === 'approved');
  const overdueCount = approved.filter(m => m.cycles_due > 0).length;
  const cycles = Array.from({ length: Math.min(totalCycles, 104) }, (_, i) => i + 1);

  const handleSendReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/tandas/${tandaId}/send-reminders`);
      toast.success(data.message);
    } catch (error) {
      toast.error('Error al enviar recordatorios: ' + (error.response?.data?.detail || ''));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card data-testid="payment-matrix-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-xl">Matriz de Pagos</CardTitle>
            <CardDescription className="mt-1.5">
              Participantes × ciclos — ciclo actual: {elapsedCycles === 0 ? 'no ha iniciado' : `${elapsedCycles} de ${totalCycles}`}
            </CardDescription>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full gap-2"
            data-testid="send-reminders-btn"
            disabled={sending || overdueCount === 0}
            onClick={handleSendReminders}
          >
            <BellRing size={14} />
            {sending ? 'Enviando...' : overdueCount === 0 ? 'Sin pagos atrasados' : `Enviar ${overdueCount} recordatorio${overdueCount > 1 ? 's' : ''}`}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {approved.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6" data-testid="matrix-empty">
            Aún no hay participantes aprobades en esta tanda.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payment-matrix-table">
                <thead>
                  <tr>
                    <th className="text-left p-2 font-medium text-muted-foreground sticky left-0 bg-card min-w-[140px]">Participante</th>
                    {cycles.map(c => (
                      <th key={c} className={`p-2 font-medium text-xs min-w-[44px] text-center ${c === elapsedCycles ? 'text-primary' : 'text-muted-foreground'}`}>
                        C{c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {approved.map(m => (
                    <tr key={m._id} className="border-t border-border" data-testid={`matrix-row-${m.user_id}`}>
                      <td className="p-2 font-medium sticky left-0 bg-card whitespace-nowrap max-w-[180px] truncate">{m.full_name}</td>
                      {cycles.map(c => (
                        <td key={c} className={`p-1.5 text-center ${c === elapsedCycles ? 'bg-primary/5' : ''}`}>
                          <Cell member={m} cycle={c} elapsed={elapsedCycles} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-4 mt-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-green-100 text-green-600 inline-flex items-center justify-center"><Check size={10} /></span> Validado</span>
              <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-yellow-100 text-yellow-600 inline-flex items-center justify-center"><Clock size={10} /></span> Por validar</span>
              <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-red-100 text-red-500 inline-flex items-center justify-center"><X size={10} /></span> Rechazado</span>
              <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-red-50 border border-dashed border-red-300 inline-block"></span> Atrasado</span>
              <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-muted inline-block"></span> Ciclo futuro</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
